import React, { useState } from 'react'

// Mui
import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  Tooltip,
} from '@material-ui/core'
import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons'

// Util
import { Observer } from 'mobx-react'
import _toArray from 'lodash/toArray'
import _size from 'lodash/size'

// My util
import useStyles from './UserAccountList.styles'
import { useStores } from 'config/store'
import SearchBar from './SearchBar'

const UserAccountList = () => {
  const classes = useStyles()
  const { accountStore } = useStores()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredAccounts = !searchTerm
    ? accountStore.accounts
    : _toArray(accountStore.accounts).filter((account) => {
        if (!searchTerm) {
          return true
        }
        const matchesSearch = account.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
        const hasMatchingChildren = _toArray(account.subAccounts).some(
          (childAccount) =>
            childAccount.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        return matchesSearch || hasMatchingChildren
      })

  const handleCheck =
    (accountId: string) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      const item = accountStore.accounts.find(
        (account: { id: string }) => account.id === accountId
      )
      if (item && item.subAccountIds.length) {
        const every = item.subAccountIds.every((subAccount: string) =>
          accountStore._selectedAccounts.includes(subAccount)
        )
        accountStore.toggleAccounts(item.subAccountIds, !every)
        return
      }
      accountStore.toggleAccount(accountId)
    }

  const handleToggleAll = () => {
    accountStore.toggleAccounts(
      accountStore.accountIdsExcludingParent,
      !accountStore.allSelected
    )
  }

  const handleToggleSection = (accountId: string) => () => {
    const newHideSections = accountStore.hideSections.slice()
    if (newHideSections.includes(accountId)) {
      newHideSections.splice(accountStore.hideSections.indexOf(accountId), 1)
    } else {
      newHideSections.push(accountId)
    }
    accountStore.hideSections = newHideSections
  }

  const renderUserSubAccountList = (account: {
    subAccountIds?: string[]
    id: string
    name: string
    subAccounts: any
  }) => {
    const filteredSubAccounts = _toArray(account.subAccounts).filter(
      (childAccount) =>
        childAccount.name.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (
      filteredSubAccounts.length === 0 &&
      !account.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return <></>
    }

    return (
      <Collapse
        in={!accountStore.hideSections.includes(account.id)}
        timeout={500}
      >
        <List component="nav" disablePadding>
          {filteredSubAccounts.map((childAccount, key) => (
            <ListItem
              key={`${childAccount.id}--${key}`}
              classes={{
                root: classes.listChild,
              }}
            >
              <FormControlLabel
                classes={{
                  root: classes.listText,
                  label: classes.listTextPrimary,
                }}
                control={
                  <Checkbox
                    classes={{
                      root: classes.checkbox,
                      checked: classes.checked,
                    }}
                    color="primary"
                    onClick={handleCheck(childAccount.id)}
                    checked={accountStore._selectedAccounts.includes(
                      childAccount.id
                    )}
                  />
                }
                label={childAccount.name.replace(`${account.name} - `, '')}
                labelPlacement="end"
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    )
  }

  return (
    <Observer
      render={() => {
        const { _selectedAccounts, hideSections, allSelected } = accountStore

        return (
          <div className={classes.root}>
            <Box className={classes.sticky}>
              <Tooltip
                title={allSelected ? 'Unselect all' : 'Select all'}
                placement="bottom-start"
                classes={{
                  tooltip: classes.tooltip,
                }}
              >
                <Box className={classes.selectAllContainer}>
                  <Checkbox
                    color="primary"
                    checked={allSelected}
                    onClick={handleToggleAll}
                    value={allSelected}
                  />
                </Box>
              </Tooltip>
              <Box sx={{ width: '100%' }}>
                <SearchBar
                  searchTerm={searchTerm}
                  onSearchChange={setSearchTerm}
                />
              </Box>
            </Box>
            {filteredAccounts.length === 0 ? (
              <div style={{ padding: '4px' }}>No accounts found</div>
            ) : (
              filteredAccounts.map(
                (
                  account: {
                    subAccountIds: any[]
                    id: any
                    name: string
                    subAccounts: string | object | null | undefined
                  },
                  key: any
                ) => {
                  const selectedCount = account.subAccountIds.reduce(
                    (acc, curr) =>
                      acc + (_selectedAccounts.includes(curr) ? 1 : 0),
                    0
                  )
                  const totalCount = account.subAccountIds.length

                  const all = 0 < totalCount && selectedCount === totalCount
                  const some = 0 < selectedCount && selectedCount < totalCount

                  const isLeafAndSelected =
                    totalCount === 0 && _selectedAccounts.includes(account.id)

                  const countDescription =
                    0 < totalCount ? ` (${selectedCount}/${totalCount})` : ''

                  return (
                    <div
                      key={`${account.id}--${key}`}
                      className={classes.accountsList}
                    >
                      <List
                        classes={{
                          root: classes.listLayout,
                        }}
                      >
                        <ListItem
                          classes={{
                            container: classes.listItemContainer,
                            root: classes.listItemRoot,
                            gutters: classes.gutters,
                          }}
                        >
                          <ListItemText
                            classes={{
                              root: classes.listItemTextRoot,
                              primary: classes.listItemTextPrimary,
                            }}
                          >
                            <FormControlLabel
                              classes={{
                                root: classes.listText,
                                label: classes.listTextPrimary,
                              }}
                              control={
                                accountStore.accounts.length < 1 ? (
                                  <></>
                                ) : (
                                  <Checkbox
                                    classes={{
                                      root: classes.checkbox,
                                      checked: classes.checked,
                                    }}
                                    color="primary"
                                    checked={all || isLeafAndSelected}
                                    indeterminate={some && !all}
                                    onClick={handleCheck(account.id)}
                                  />
                                )
                              }
                              label={account.name + countDescription}
                            />
                            {account.subAccounts &&
                              _size(account.subAccounts) > 0 && (
                                <ListItemSecondaryAction
                                  classes={{
                                    root: classes.listItemSecondaryActionRoot,
                                  }}
                                >
                                  <IconButton
                                    color="primary"
                                    onClick={handleToggleSection(account.id)}
                                    classes={{
                                      root: classes.dropButton,
                                    }}
                                  >
                                    {!hideSections.includes(account.id) ? (
                                      <ArrowDropUp />
                                    ) : (
                                      <ArrowDropDown />
                                    )}
                                  </IconButton>
                                </ListItemSecondaryAction>
                              )}
                          </ListItemText>
                        </ListItem>
                      </List>
                      {account.subAccounts &&
                        _size(account.subAccounts) > 0 &&
                        renderUserSubAccountList(account)}
                    </div>
                  )
                }
              )
            )}
          </div>
        )
      }}
    />
  )
}

export default UserAccountList
